import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import PrintIcon from '@mui/icons-material/Print';
import { Alert, Box, Button, Card, GlobalStyles, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import useBackendApi from '../../../../../hooks/useBackendApi';
import { EventFormQrCodeContext } from '../../../../../types/api/accountEvents';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type QrCodeScope = 'event' | 'attendee';

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  event_id: string;
  form_id?: string;
  guest_id?: string;
  requirement_id?: string;
};

type EventFormQrCodePageProps = {
  scope: QrCodeScope;
};

const isQrCodeContext = (value: unknown): value is EventFormQrCodeContext => {
  if (!value || typeof value !== 'object') return false;

  const context = value as Partial<EventFormQrCodeContext>;
  return (
    typeof context.form_url === 'string' &&
    Boolean(context.form_url.trim()) &&
    typeof context.form_name === 'string' &&
    Boolean(context.form_name.trim()) &&
    typeof context.account_name === 'string' &&
    Boolean(context.account_name.trim()) &&
    (context.scope === 'event' || context.scope === 'attendee')
  );
};

export default function EventFormQrCodePage({ scope }: EventFormQrCodePageProps): JSX.Element {
  const {
    org_path_name,
    acc_path_name,
    event_id,
    form_id,
    guest_id,
    requirement_id,
  } = useParams<RouteParams>() as RouteParams;
  const navigate = useNavigate();
  const { apiFetchRequest } = useBackendApi();
  const [context, setContext] = useState<EventFormQrCodeContext | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const eventPath = useMemo(
    () =>
      `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/events/${encodeURIComponent(event_id)}`,
    [acc_path_name, event_id, org_path_name],
  );

  const endpoint = useMemo(() => {
    const eventEndpoint = `${backendURL}${eventPath}`;
    if (scope === 'event' && form_id) {
      return `${eventEndpoint}/forms/${encodeURIComponent(form_id)}/qr-code`;
    }
    if (scope === 'attendee' && guest_id && requirement_id) {
      return `${eventEndpoint}/guests/${encodeURIComponent(
        guest_id,
      )}/requirements/${encodeURIComponent(requirement_id)}/qr-code`;
    }
    return null;
  }, [eventPath, form_id, guest_id, requirement_id, scope]);

  useEffect(() => {
    let cancelled = false;

    const loadQrCodeContext = async (): Promise<void> => {
      if (!endpoint) {
        setErrorText('The form QR code link is incomplete.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorText(null);
      try {
        const response = await apiFetchRequest({ endpoint, method: 'GET' });
        if (cancelled) return;

        if (response?.status === 401 || response?.status === 403) {
          navigate('/401.html');
          return;
        }
        if (!response || response.isError || !isQrCodeContext(response.body)) {
          setErrorText(
            response?.status === 404
              ? 'This form is no longer available.'
              : 'Unable to load the form QR code. Please try again.',
          );
          return;
        }

        if (response.body.scope !== scope) {
          setErrorText('The form QR code response did not match this page.');
          return;
        }
        setContext(response.body);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load form QR code context', error);
          setErrorText('Unable to load the form QR code. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadQrCodeContext();
    return () => {
      cancelled = true;
    };
  }, [apiFetchRequest, endpoint, navigate, scope]);

  const handleCopyLink = useCallback(async () => {
    if (!context) return;

    try {
      await navigator.clipboard.writeText(context.form_url);
      setCopyMessage('Form link copied.');
    } catch (error) {
      console.error('Failed to copy form link', error);
      setCopyMessage('Unable to copy the form link.');
    }
  }, [context]);

  if (isLoading) {
    return (
      <GradientCircularProgress
        loadingMessage="Loading form QR code..."
        containerSxProps={{ py: 8, textAlign: 'center' }}
      />
    );
  }

  if (!context || errorText) {
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto', px: 2, py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorText || 'Unable to load the form QR code.'}
        </Alert>
        <Button component={RouterLink} startIcon={<ArrowBackIcon />} to={eventPath}>
          Return to Event
        </Button>
      </Box>
    );
  }

  const isAttendeeQrCode = context.scope === 'attendee';

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#form-qr-print-area, #form-qr-print-area *': { visibility: 'visible' },
            '#form-qr-print-area': {
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
            },
            '.form-qr-page-actions': { display: 'none !important' },
          },
        }}
      />
      <Box sx={{ maxWidth: 680, mx: 'auto', px: 2, py: { xs: 3, md: 5 } }}>
        <Card
          id="form-qr-print-area"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: 2,
            px: { xs: 2.5, sm: 5 },
            py: { xs: 3, sm: 5 },
            textAlign: 'center',
          }}
        >
          <Typography color="primary" fontWeight={700} letterSpacing="0.12em" variant="overline">
            {isAttendeeQrCode ? 'EMPLOYEE FORM' : 'EVENT FORM'}
          </Typography>
          <Typography component="h1" sx={{ mt: 1 }} variant="h4">
            {context.form_name}
          </Typography>
          <Typography color="text.secondary" fontWeight={500} sx={{ mt: 1 }} variant="subtitle1">
            Account: {context.account_name}
          </Typography>

          {isAttendeeQrCode ? (
            <>
              <Typography sx={{ mt: 1.5 }} variant="h6">
                For {context.attendee_name || 'Attendee'}
              </Typography>
              <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
                This QR code is unique to this employee. Do not share or reuse it for another attendee.
              </Alert>
            </>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
              This shared QR code opens the event-level form.
            </Typography>
          )}

          <Box
            sx={{
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              display: 'inline-flex',
              mt: 4,
              p: 2,
            }}
          >
            <QRCodeSVG
              bgColor="#FFFFFF"
              fgColor="#0F172A"
              level="H"
              marginSize={4}
              size={360}
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
              title={`${context.form_name} QR code`}
              value={context.form_url}
            />
          </Box>

          <Typography fontWeight={600} sx={{ mt: 3 }}>
            Scan with a mobile camera to open the form
          </Typography>

          <Box
            className="form-qr-page-actions"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mt: 4 }}
          >
            <Button
              component="a"
              href={context.form_url}
              rel="noopener noreferrer"
              startIcon={<LaunchIcon />}
              target="_blank"
              variant="contained"
            >
              Open Form
            </Button>
            <Button onClick={() => void handleCopyLink()} startIcon={<ContentCopyIcon />} variant="outlined">
              Copy Link
            </Button>
            <Button onClick={() => window.print()} startIcon={<PrintIcon />} variant="outlined">
              Print
            </Button>
            <Button component={RouterLink} startIcon={<ArrowBackIcon />} to={eventPath} variant="text">
              Return to Event
            </Button>
          </Box>

          {copyMessage ? (
            <Typography className="form-qr-page-actions" color="text.secondary" sx={{ mt: 2 }} variant="body2">
              {copyMessage}
            </Typography>
          ) : null}
        </Card>
      </Box>
    </>
  );
}
